import {
  ComponentProps,
  createEffect,
  createMemo,
  createSignal,
  JSX,
  onCleanup,
  untrack,
} from "solid-js";

import Cleanup from "@repo/utils/Cleanup";
import UString from "@repo/utils/UString";
import UHTMLElement from "@repo/utils-client/UHTMLElement";

declare namespace Expand {
  type Props = Omit<ComponentProps<"div">, "style"> & {
    class?: string;
    style?: JSX.CSSProperties;
    direction: "x" | "y" | "xy";
    forFade?: boolean;
    show?: unknown;
    transition?: JSX.CSSProperties["transition"];
    children: JSX.Element;
  };
}

function Expand({
  direction,
  style,
  children,
  transition,
  forFade,
  ...props
}: D<Expand.Props>) {
  const show = createMemo(() => ("show" in props ? props.show : true));
  const [dimensions, setDimensions] = createSignal<{
    width?: number;
    height?: number;
  } | null>({
    width: direction.includes("x") ? 0 : undefined,
    height: direction.includes("y") ? 0 : undefined,
  });

  let ref: HTMLDivElement = null!;

  createEffect(() => {
    const dir = untrack(() => direction);
    const x = dir.includes("x");
    const y = dir.includes("y");
    const { scrollWidth, scrollHeight } = ref.firstChild as HTMLElement;
    const width = x ? scrollWidth : undefined;
    const height = y ? scrollHeight : undefined;

    if (show()) {
      const cleanup = new Cleanup();
      onCleanup(() => cleanup.dispose());

      requestAnimationFrame(() => {
        if (cleanup.disposed) return;
        setDimensions({ width, height });
      });

      cleanup.setTimeout(() => {
        setDimensions(null);
        cleanup.dispose();
      }, UHTMLElement.computeTotalTransitionMs(ref));
    } else {
      if (untrack(dimensions) == null) setDimensions({ width, height });
      const raf = requestAnimationFrame(() => {
        setDimensions({
          width: x ? 0 : undefined,
          height: y ? 0 : undefined,
        });
      });
      onCleanup(() => {
        cancelAnimationFrame(raf);
      });
    }
  });

  return (
    <div
      ref={ref}
      {...props}
      style={{
        transition: transition || "var(--normal-s)",
        ...(forFade
          ? { position: "relative" }
          : { overflow: dimensions() == null ? undefined : "hidden" }),
        width: UString.withAllValues`${dimensions()?.width}px`,
        height: UString.withAllValues`${dimensions()?.height}px`,
        ...style,
      }}
    >
      <div
        // NOTE: If this breaks something, comment exactly why it can't be
        // included, removing will introduce jank in search bar key bind prompt
        // and potentially other places
        style={{ "min-width": "fit-content" }}
      >
        {children}
      </div>
    </div>
  );
}

export default Expand;
